
import React from 'react';
import * as LucideIcons from 'lucide-react';
import { Edit3, Sparkles, Loader2, Calendar, Briefcase, Leaf } from 'lucide-react';
import { SiteContent } from '../types';
import { optimizeContentAI } from '../services/geminiService';

interface TravelTipsProps {
  data: SiteContent['tips'];
  isAdmin: boolean;
  onUpdate: (data: SiteContent['tips']) => void;
}

const TravelTips: React.FC<TravelTipsProps> = ({ data, isAdmin, onUpdate }) => {
  const [isOptimizing, setIsOptimizing] = React.useState<string | null>(null);

  const handleOptimize = async (field: keyof SiteContent['tips']) => {
    setIsOptimizing(field as string);
    const optimized = await optimizeContentAI(data[field] as string, `Travel Tips ${field}`);
    onUpdate({ ...data, [field]: optimized });
    setIsOptimizing(null);
  };

  const updatePrinciple = (id: string, updates: any) => {
    onUpdate({
      ...data,
      responsiblePrinciples: data.responsiblePrinciples.map(p => p.id === id ? { ...p, ...updates } : p)
    });
  };

  const updatePackingItem = (id: string, updates: any) => {
    onUpdate({
      ...data,
      packingList: data.packingList.map(item => item.id === id ? { ...item, ...updates } : item)
    });
  };

  return (
    <section id="tips" className="relative py-24 bg-white overflow-hidden scroll-mt-20">
      <div className="container mx-auto px-6 relative z-10">
        
        {/* Header Section - No Panel */}
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
          <span className="text-sky-500 font-bold tracking-luxury uppercase text-[9px] mb-6 block">Expedition Intel</span>
          
          {isAdmin ? (
            <div className="p-8 border-2 border-dashed border-slate-100 rounded-[2.5rem] space-y-4 bg-slate-50/50">
              <div className="flex items-center gap-2 text-slate-400 font-bold text-[9px] justify-center uppercase tracking-luxury"><Edit3 className="w-4 h-4" /> Editorial</div>
              <input 
                value={data.title}
                onChange={e => onUpdate({...data, title: e.target.value})}
                className="w-full text-3xl font-bold text-center text-slate-900 font-serif italic focus:outline-none bg-transparent"
              />
              <textarea 
                value={data.description}
                onChange={e => onUpdate({...data, description: e.target.value})}
                className="w-full text-slate-500 text-center text-lg focus:outline-none h-20 bg-transparent font-light leading-relaxed"
              />
            </div>
          ) : (
            <>
              <h2 className="text-4xl md:text-5xl font-bold mb-8 text-slate-900 font-serif italic tracking-tighter leading-none">
                Plan Your <span className="text-sky-500 block mt-2">Perfect Escape</span>
              </h2>
              <p className="text-slate-500 text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
                {data.description}
              </p>
            </>
          )}
        </div>

        {/* Info Grid - Open Columns with Border Separators */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 lg:gap-0">
          
          {/* Best Time to Visit */}
          <div className="lg:px-12 group border-slate-100 lg:border-r">
            <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mb-10 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
              <Calendar className="text-amber-500 w-7 h-7" strokeWidth={1.5} />
            </div>
            {isAdmin ? (
              <input 
                value={data.bestTimeTitle}
                onChange={e => onUpdate({...data, bestTimeTitle: e.target.value})}
                className="w-full bg-transparent text-2xl font-bold mb-8 text-slate-900 font-serif italic focus:outline-none"
              />
            ) : (
              <h3 className="text-2xl font-bold mb-8 text-slate-900 font-serif italic">{data.bestTimeTitle}</h3>
            )}
            
            <div className="space-y-10">
              <div>
                <div className="flex justify-between mb-3">
                  <span className="font-bold text-slate-400 text-[9px] uppercase tracking-widest">Peak Rhythms</span>
                  <span className="text-amber-500 text-[9px] font-bold uppercase tracking-widest">{data.peakMonths}</span>
                </div>
                <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden">
                  <div className="bg-amber-400 h-full w-[100%] transition-all duration-1000"></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-3">
                  <span className="font-bold text-slate-400 text-[9px] uppercase tracking-widest">Quiet Passage</span>
                  <span className="text-slate-400 text-[9px] font-bold uppercase tracking-widest">{data.offPeakMonths}</span>
                </div>
                <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden">
                  <div className="bg-slate-300 h-full w-[40%]"></div>
                </div>
              </div>
            </div>
          </div>

          {/* What to Pack */}
          <div className="lg:px-12 group border-slate-100 lg:border-r">
            <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mb-10 group-hover:scale-110 group-hover:-rotate-3 transition-all duration-500">
              <Briefcase className="text-sky-500 w-7 h-7" strokeWidth={1.5} />
            </div>
            {isAdmin ? (
              <input 
                value={data.packingTitle}
                onChange={e => onUpdate({...data, packingTitle: e.target.value})}
                className="w-full bg-transparent text-2xl font-bold mb-8 text-slate-900 font-serif italic focus:outline-none"
              />
            ) : (
              <h3 className="text-2xl font-bold mb-8 text-slate-900 font-serif italic">{data.packingTitle}</h3>
            )}
            
            <ul className="space-y-4">
              {data?.packingList?.map((item) => {
                const Icon = (LucideIcons as any)[item.icon] || LucideIcons.Package;
                return (
                  <li key={item.id} className="flex items-center gap-4 group/item">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-100 group-hover/item:border-sky-200 group-hover/item:bg-sky-50 transition-all">
                      <Icon className="w-4 h-4 text-slate-400 group-hover/item:text-sky-500" strokeWidth={1.5} />
                    </div>
                    <span className="text-slate-600 font-medium text-xs tracking-wide">{item.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Responsible Tourism */}
          <div className="lg:px-12 group">
            <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mb-10 group-hover:scale-110 transition-all duration-500">
              <Leaf className="text-green-500 w-7 h-7" strokeWidth={1.5} />
            </div>
            {isAdmin ? (
              <input 
                value={data.responsibleTitle}
                onChange={e => onUpdate({...data, responsibleTitle: e.target.value})}
                className="w-full bg-transparent text-2xl font-bold mb-8 text-slate-900 font-serif italic focus:outline-none"
              />
            ) : (
              <h3 className="text-2xl font-bold mb-8 text-slate-900 font-serif italic">{data.responsibleTitle}</h3>
            )}
            
            <div className="space-y-8">
              {data?.responsiblePrinciples?.map((p) => {
                const Icon = (LucideIcons as any)[p.icon] || LucideIcons.Info;
                return (
                  <div key={p.id} className="flex gap-4 group/item">
                    <Icon className="w-5 h-5 text-green-500 flex-shrink-0 group-hover/item:rotate-12 transition-transform" strokeWidth={1.5} />
                    <div>
                      <strong className="text-slate-800 block mb-1.5 text-[11px] font-bold tracking-wide uppercase">{p.title}</strong>
                      <p className="text-slate-500 text-xs leading-relaxed font-light">{p.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
      
      {/* Decorative Accents */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-slate-50/40 blur-[130px] rounded-full -z-10"></div>
    </section>
  );
};

export default TravelTips;
